import { BACKEND_URL } from "../../lib/backend";
 
/**
 * DiscoveryStatsWidget — health dashboard for discovery-only curators.
 *
 * Surfaces, per curator (Travel AI Playbook, PhocusWire, WiT, Skift Take, …):
 *   - articles surfaced (24h / 7d)
 *   - reports surfaced (24h / 7d)
 *   - strategy mix breakdown (anchor_keyword / known_publisher / first_external / llm_fallback)
 *   - last scan timestamp + pattern
 *   - 3 most-recent samples per curator
 *   - estimated LLM cost over the 7d window
 *
 * Lives inside `SourcesTab` above the regular News Sources stats row.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Telescope, ExternalLink, AlertCircle, PlayCircle, Trash2 } from "lucide-react";
import { Switch } from "../ui/switch";
import { useAuth } from "../../AuthContext";

const STRATEGY_COLORS = {
  anchor_keyword:    { bg: "#dbeafe", fg: "#1e40af", label: "Anchor" },
  known_publisher:   { bg: "#dcfce7", fg: "#166534", label: "Known publ." },
  first_external:    { bg: "#fef3c7", fg: "#92400e", label: "First ext." },
  llm_fallback:      { bg: "#f3e8ff", fg: "#6b21a8", label: "LLM" },
  other:             { bg: "#e5e7eb", fg: "#374151", label: "Other" },
};

const STRATEGY_COLORS_DARK = {
  anchor_keyword:    { bg: "#1e3a5f", fg: "#93c5fd", label: "Anchor" },
  known_publisher:   { bg: "#14352a", fg: "#86efac", label: "Known publ." },
  first_external:    { bg: "#3f2e0e", fg: "#fcd34d", label: "First ext." },
  llm_fallback:      { bg: "#3b1e5e", fg: "#d8b4fe", label: "LLM" },
  other:             { bg: "#2a3132", fg: "#cbd5e1", label: "Other" },
};

function fmtRelative(iso) {
  if (!iso) return "never scanned";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.round(ms / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const h = Math.floor(mins / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return iso; }
}

export default function DiscoveryStatsWidget({ dm = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { authHeader } = useAuth();

  const API = BACKEND_URL + "/api";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/discovery-stats`, {
        headers: authHeader(),
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [API, authHeader]);

  useEffect(() => { load(); }, [load]);

  // Action handlers: bound to user clicks. Feedback via sonner toast (no
  // per-row busy state — the toast lifecycle already signals progress).
  function runScanNow(name) {
    const toastId = toast.loading(`Scanning "${name}"… (this can take 30-60s)`);
    axios.post(
      `${API}/admin/discovery-stats/scan/${encodeURIComponent(name)}`,
      {},
      { headers: authHeader(), withCredentials: true, timeout: 180000 },
    ).then((r) => {
      const quotaErr = r.data.firecrawl_quota_error;
      const scraped = r.data.articles_scraped ?? 0;
      const saved = r.data.articles_saved ?? 0;
      const reports = r.data.reports_inserted ?? 0;
      if (quotaErr && scraped === 0) {
        // Firecrawl ran out of credits — don't mislead with a green +0/0 toast
        toast.error(
          `${name}: Firecrawl quota exceeded. Add credits at firecrawl.dev/pricing.`,
          { id: toastId, duration: 10000 },
        );
      } else if (scraped === 0 && saved === 0 && reports === 0) {
        toast.warning(
          `${name}: 0 new articles found (all URLs already ingested, or no /p/ links matched).`,
          { id: toastId, duration: 8000 },
        );
      } else {
        toast.success(
          `${name}: scraped ${scraped} · saved ${saved} articles, +${reports} reports`,
          { id: toastId },
        );
      }
      load();
    }).catch((e) => {
      toast.error(
        `Scan failed: ${e?.response?.data?.detail || e.message}`,
        { id: toastId },
      );
    });
  }

  function toggleEnabled(name, enabled) {
    // Optimistic UI update — revert on failure
    setData((d) => d && ({
      ...d,
      curators: d.curators.map((c) => c.name === name ? { ...c, enabled } : c),
    }));
    axios.patch(
      `${API}/admin/discovery-stats/enable/${encodeURIComponent(name)}`,
      { enabled },
      { headers: authHeader(), withCredentials: true },
    ).then(() => {
      toast.success(`${name}: ${enabled ? "enabled" : "disabled"}`);
    }).catch((e) => {
      toast.error(`Toggle failed: ${e?.response?.data?.detail || e.message}`);
      // Revert
      setData((d) => d && ({
        ...d,
        curators: d.curators.map((c) => c.name === name ? { ...c, enabled: !enabled } : c),
      }));
    });
  }

  /** Cleanup hygiene job: deletes misclassified reports + sanitizes curator
   *  URL leaks in existing summaries. Two-step UX: dry-run → confirm → apply. */
  function runCleanup() {
    const toastId = toast.loading("Scanning DB for issues…");
    axios.post(
      `${API}/admin/discovery-stats/cleanup-misclassified-reports`,
      {},
      { headers: authHeader(), withCredentials: true },
    ).then((r) => {
      const d = r.data;
      const total =
        (d.misclassified_reports_count || 0) +
        (d.leaky_reports_count || 0) +
        (d.leaky_articles_count || 0);
      if (total === 0) {
        toast.success("No issues found — DB is clean.", { id: toastId });
        return;
      }
      toast.dismiss(toastId);
      const msg = `Found:\n• ${d.misclassified_reports_count} misclassified reports\n• ${d.leaky_reports_count} reports with curator URL leak\n• ${d.leaky_articles_count} articles with curator URL leak\n\nApply cleanup? (deletes misclassified, sanitizes the rest)`;
      if (!window.confirm(msg)) return;
      const applyId = toast.loading("Applying cleanup…");
      axios.post(
        `${API}/admin/discovery-stats/cleanup-misclassified-reports?apply=true`,
        {},
        { headers: authHeader(), withCredentials: true },
      ).then((r2) => {
        const dd = r2.data;
        toast.success(
          `Cleanup done: deleted ${dd.deleted_misclassified_reports || 0}, sanitized ${(dd.sanitized_reports || 0) + (dd.sanitized_articles || 0)}.`,
          { id: applyId, duration: 8000 },
        );
        load();
      }).catch((e) => {
        toast.error(`Apply failed: ${e?.response?.data?.detail || e.message}`, { id: applyId });
      });
    }).catch((e) => {
      toast.error(`Cleanup scan failed: ${e?.response?.data?.detail || e.message}`, { id: toastId });
    });
  }

  const palette = dm ? STRATEGY_COLORS_DARK : STRATEGY_COLORS;

  return (
    <div
      data-testid="discovery-stats-widget"
      className={`rounded-2xl overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`}
      style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}
    >
      {/* Header */}
      <div className={`px-5 py-3 border-b flex items-center justify-between ${dm ? "border-white/5" : "border-black/4"}`}>
        <div className="flex items-center gap-2">
          <Telescope className={`w-4 h-4 ${dm ? "text-purple-400" : "text-[#6b21a8]"}`} />
          <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
            Discovery curators
          </p>
          {data?.curators && (
            <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
              {data.curators.length} active · last 7d
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runCleanup}
            data-testid="discovery-cleanup-btn"
            title="Scan & sanitize: deletes misclassified reports + strips curator URLs from summaries"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md category-tag transition-colors ${dm ? "bg-amber-900/30 text-amber-300 hover:bg-amber-900/50" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Clean issues
          </button>
          <button
            onClick={load}
            disabled={loading}
            data-testid="discovery-stats-refresh-btn"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md category-tag transition-colors disabled:opacity-50 ${dm ? "bg-white/5 text-slate-400 hover:bg-white/10" : "bg-[#f3f4f5] text-[#6b7a7d] hover:bg-[#e8ecee]"}`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Persistent quota-error banner */}
      {data?.firecrawl_quota_error && (
        <div
          data-testid="discovery-firecrawl-quota-banner"
          className={`px-5 py-3 flex items-start gap-2 border-b ${dm ? "bg-amber-900/20 border-amber-700/30 text-amber-300" : "bg-amber-50 border-amber-200 text-amber-900"}`}
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-semibold">Firecrawl quota exceeded</p>
            <p className="text-xs opacity-90">
              Discovery scans cannot run. Add credits at{" "}
              <a
                href="https://firecrawl.dev/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-semibold"
              >
                firecrawl.dev/pricing
              </a>
              {" "}or downgrade your plan limits. Last error: {data.firecrawl_quota_error.when?.slice(0, 19)}Z
            </p>
          </div>
        </div>
      )}

      {/* Body */}
      {error ? (
        <div className={`p-5 flex items-center gap-2 ${dm ? "text-red-400" : "text-red-700"}`}>
          <AlertCircle className="w-4 h-4" /> <span className="text-sm">{String(error)}</span>
        </div>
      ) : !data ? (
        <div className={`p-6 text-center category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
          {loading ? "Loading discovery stats…" : "—"}
        </div>
      ) : data.curators.length === 0 ? (
        <div className={`p-6 text-center category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
          No discovery curators configured yet.
        </div>
      ) : (
        <>
          {/* Totals strip */}
          <div className={`px-5 py-3 grid grid-cols-2 md:grid-cols-4 gap-4 border-b ${dm ? "border-white/5 bg-[#141a1b]" : "border-black/4 bg-[#fafbfc]"}`}>
            <Totals dm={dm} label="Articles 24h" value={data.totals.articles_24h} testid="discovery-total-articles-24h" />
            <Totals dm={dm} label="Articles 7d"  value={data.totals.articles_7d}  testid="discovery-total-articles-7d" />
            <Totals dm={dm} label="Reports 7d"   value={data.totals.reports_7d}   testid="discovery-total-reports-7d" />
            <Totals dm={dm} label="LLM cost 7d"  value={`$${(data.llm_cost_estimate_7d_usd || 0).toFixed(4)}`} testid="discovery-total-llm-cost-7d" hint={`${data.totals.strategies.llm_fallback} calls`} />
          </div>

          {/* Per-curator rows */}
          {data.curators.map((cur, idx) => {
            const totalStrat = Object.values(cur.strategy_mix_7d).reduce((a, b) => a + b, 0);
            return (
              <div
                key={cur.name}
                data-testid={`discovery-curator-row-${idx}`}
                className={`px-5 py-4 border-b last:border-b-0 ${dm ? "border-white/4" : "border-black/3"}`}
              >
                <div className="grid grid-cols-12 gap-3 items-start">
                  {/* Left: name + url + last scan */}
                  <div className="col-span-12 md:col-span-5">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
                        {cur.name}
                      </p>
                      {/* Enable/disable toggle */}
                      <Switch
                        checked={!!cur.enabled}
                        onCheckedChange={(v) => toggleEnabled(cur.name, v)}
                        data-testid={`discovery-toggle-${idx}`}
                        aria-label={`${cur.enabled ? "Disable" : "Enable"} ${cur.name}`}
                      />
                      {/* Run scan now */}
                      <button
                        onClick={() => runScanNow(cur.name)}
                        disabled={!cur.enabled}
                        data-testid={`discovery-scan-now-${idx}`}
                        title={cur.enabled ? "Run a one-off scan now (bypasses 24h cadence)" : "Enable the curator first"}
                        className={`category-tag flex items-center gap-1 px-2 py-1 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${dm ? "bg-purple-900/30 text-purple-300 hover:bg-purple-900/50" : "bg-purple-50 text-purple-700 hover:bg-purple-100"}`}
                      >
                        <PlayCircle className="w-3 h-3" />
                        Scan now
                      </button>
                      {!cur.enabled && (
                        <span className={`category-tag px-2 py-0.5 rounded-full ${dm ? "bg-red-900/30 text-red-300" : "bg-red-50 text-red-700"}`}>
                          disabled
                        </span>
                      )}
                    </div>
                    {cur.index_url && (
                      <a href={cur.index_url} target="_blank" rel="noopener noreferrer"
                         className={`category-tag inline-flex items-center gap-1 hover:underline ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                        {cur.index_url.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <p className={`category-tag mt-1 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
                      Last scan: {fmtRelative(cur.last_scan_at)}
                      {cur.pattern && ` · pattern: ${cur.pattern}`}
                    </p>
                  </div>

                  {/* Middle: numeric counts */}
                  <div className="col-span-6 md:col-span-3 flex gap-4">
                    <div>
                      <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Articles</p>
                      <p className={`text-xl font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`} style={{ fontFamily: "'Reckless Neue Variable', serif" }}>
                        {cur.articles_24h}<span className={`text-xs font-normal ml-1 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>/ {cur.articles_7d}</span>
                      </p>
                      <p className={`category-tag text-[10px] ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>24h / 7d</p>
                    </div>
                    <div>
                      <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Reports</p>
                      <p className={`text-xl font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`} style={{ fontFamily: "'Reckless Neue Variable', serif" }}>
                        {cur.reports_24h}<span className={`text-xs font-normal ml-1 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>/ {cur.reports_7d}</span>
                      </p>
                      <p className={`category-tag text-[10px] ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>24h / 7d</p>
                    </div>
                  </div>

                  {/* Right: strategy mix */}
                  <div className="col-span-12 md:col-span-4">
                    <p className={`category-tag mb-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                      Strategy mix 7d {totalStrat > 0 && `(${totalStrat})`}
                    </p>
                    {totalStrat === 0 ? (
                      <p className={`category-tag ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>—</p>
                    ) : (
                      <div className="flex h-2 rounded-full overflow-hidden mb-2" data-testid={`discovery-strategy-bar-${idx}`}>
                        {Object.entries(cur.strategy_mix_7d).map(([k, v]) => {
                          if (v === 0) return null;
                          const pct = (v / totalStrat) * 100;
                          return (
                            <div key={k}
                              title={`${palette[k]?.label || k}: ${v} (${pct.toFixed(0)}%)`}
                              style={{ width: `${pct}%`, background: palette[k]?.fg || "#888" }}
                            />
                          );
                        })}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(cur.strategy_mix_7d).map(([k, v]) => v > 0 && (
                        <span key={k}
                          className="category-tag px-2 py-0.5 rounded-full"
                          style={{ background: palette[k]?.bg, color: palette[k]?.fg }}
                        >
                          {palette[k]?.label || k}: {v}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Recent samples */}
                {cur.recent_samples && cur.recent_samples.length > 0 && (
                  <div className={`mt-3 pt-3 border-t ${dm ? "border-white/4" : "border-black/3"}`}>
                    <p className={`category-tag mb-2 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Recent surfaced</p>
                    <div className="space-y-1.5">
                      {cur.recent_samples.map((s, i) => (
                        <a key={i} href={s.originalUrl} target="_blank" rel="noopener noreferrer"
                           className={`flex items-start gap-2 text-xs hover:underline ${dm ? "text-slate-300" : "text-[#191c1d]"}`}
                           data-testid={`discovery-sample-${idx}-${i}`}>
                          <span className={`shrink-0 category-tag mt-0.5 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
                            {s.source}
                          </span>
                          <span className="flex-1 line-clamp-1">{s.title}</span>
                          <ExternalLink className={`w-3 h-3 mt-0.5 shrink-0 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`} />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

function Totals({ dm, label, value, testid, hint }) {
  return (
    <div data-testid={testid}>
      <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{label}</p>
      <p className={`text-2xl font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`} style={{ fontFamily: "'Reckless Neue Variable', serif" }}>
        {value}
      </p>
      {hint && <p className={`category-tag text-[10px] ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>{hint}</p>}
    </div>
  );
}
